import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, Mail, Globe, FileText } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { LEGAL, LEGAL_META } from "../i18n/legal";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { ASSETS } from "../assets";

/**
 * Shared dark institutional shell for legal pages (Terms, Privacy).
 * Sticky table-of-contents sidebar, terminal/cyber-finance aesthetic.
 * Purely additive — does not alter any existing landing/academy components.
 */
export const LegalLayout = ({ pageKey, accent = "amber" }) => {
  const { lang } = useLanguage();
  const L = (LEGAL[lang] || LEGAL.en || LEGAL.es);
  const page = L[pageKey];
  const common = L.common;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pageKey]);

  if (!page) return null;

  const accentRing = accent === "cyan" ? "ring-cyan-400/30" : "ring-amber-400/30";
  const accentGlow = accent === "cyan"
    ? "bg-[radial-gradient(circle_at_50%_0%,rgba(34,211,238,0.18),transparent_55%)]"
    : "bg-[radial-gradient(circle_at_50%_0%,rgba(251,191,36,0.16),transparent_55%)]";
  const accentText = accent === "cyan" ? "text-cyan-300" : "text-amber-300";
  const accentBorder = accent === "cyan" ? "border-cyan-400/25" : "border-amber-400/25";
  const accentDot = accent === "cyan" ? "bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)]" : "bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.8)]";

  return (
    <div className="min-h-screen bg-black text-white antialiased" data-testid={`legal-page-${pageKey}`}>
      <Navbar />

      {/* HERO */}
      <section className="relative w-full overflow-hidden pb-12 pt-32 md:pt-40">
        <div className={`absolute inset-0 ${accentGlow}`} />
        <div className="absolute inset-x-0 top-0 mx-auto h-px max-w-5xl bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        {/* subtle terminal grid */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage: "radial-gradient(circle at 50% 0%, black, transparent 70%)",
          }}
        />

        <div className="relative mx-auto max-w-5xl px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-white/50 transition-colors hover:text-white"
              data-testid="legal-back-home"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {common.back_home}
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className={`mt-6 inline-flex items-center gap-2 rounded-full border ${accentBorder} bg-white/[0.03] px-3 py-1.5 backdrop-blur`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${accentDot}`} />
            <span className={`text-[10px] font-bold uppercase tracking-[0.28em] ${accentText} md:text-xs`}>
              {common.legal_eyebrow}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-5 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl"
            data-testid="legal-title"
          >
            {page.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-4 max-w-3xl text-base leading-relaxed text-white/55 md:text-lg"
          >
            {page.subtitle}
          </motion.p>

          {/* meta strip */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[11px] uppercase tracking-[0.22em] text-white/40"
          >
            <span className="inline-flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              {common.last_updated}: {LEGAL_META.last_updated}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Globe className="h-3.5 w-3.5" />
              {LEGAL_META.website}
            </span>
            <a
              href={`mailto:${LEGAL_META.contact_email}`}
              className="inline-flex items-center gap-1.5 transition-colors hover:text-white"
              data-testid="legal-meta-email"
            >
              <Mail className="h-3.5 w-3.5" />
              {LEGAL_META.contact_email}
            </a>
          </motion.div>
        </div>
      </section>

      {/* BODY */}
      <section className="relative w-full bg-black pb-32">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 md:px-12 lg:grid-cols-12">
          {/* TOC */}
          <aside className="lg:col-span-3" data-testid="legal-toc">
            <div className="sticky top-28">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">
                {common.table_of_contents}
              </p>
              <nav className="mt-4 border-l border-white/10">
                {page.sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className={`block border-l-2 border-transparent py-1.5 pl-4 -ml-px text-xs text-white/45 transition-all hover:border-white/40 hover:text-white`}
                    data-testid={`legal-toc-${s.id}`}
                  >
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <article className={`relative rounded-3xl border border-white/10 bg-white/[0.025] p-8 backdrop-blur-xl ring-1 ${accentRing} md:p-12 lg:col-span-9`}>
            {page.sections.map((s, i) => (
              <section
                key={s.id}
                id={s.id}
                className={`${i === 0 ? "pt-0" : "pt-12"} scroll-mt-28`}
              >
                <h2 className="font-display text-2xl font-bold tracking-tight text-white md:text-3xl">
                  {s.title}
                </h2>
                <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-white/70 md:text-base">
                  {s.paragraphs.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
                {i < page.sections.length - 1 && (
                  <div className="mt-12 h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                )}
              </section>
            ))}

            {/* Educational disclaimer banner */}
            <div className={`mt-14 flex items-start gap-3 rounded-2xl border ${accentBorder} bg-white/[0.03] p-5`}>
              <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${accentBorder} bg-black/40`}>
                <ShieldCheck className={`h-4 w-4 ${accentText}`} />
              </div>
              <div>
                <p className={`text-[10px] font-bold uppercase tracking-[0.28em] ${accentText}`}>
                  {LEGAL_META.entity}
                </p>
                <p className="mt-1 text-sm text-white/60">{common.educational_disclaimer}</p>
              </div>
            </div>

            {/* Contact CTA */}
            <a
              href={`mailto:${LEGAL_META.contact_email}`}
              className="group mt-8 inline-flex items-center gap-3 rounded-full bg-white px-6 py-3 text-sm font-bold text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,255,255,0.25)]"
              data-testid="legal-contact-cta"
            >
              <Mail className="h-4 w-4" />
              <span>{common.contact_us}</span>
            </a>
          </article>
        </div>

        {/* faint REBEL watermark */}
        <div className="pointer-events-none absolute right-6 bottom-6 hidden items-center gap-2 opacity-30 md:flex">
          <img src={ASSETS.logos.rebel} alt="" className="h-6 w-auto" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-white/30">{LEGAL_META.entity}</span>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LegalLayout;
